import { utcToZonedTime } from "date-fns-tz";
import format from "date-fns/format";
import appConstants from "@appConstants";
import { Image } from "../../types";
import { formatAddress } from "../../../../../modules/Dispatch/utils/utils";
import ReverseGeocodingBatch from "../../../../../../lib/Map/Trimble/utils/ReverseGeocodingBatch";
import { extractTimeStampLatLongValuesFromURL } from "./imageUtils";

export interface ImageURLDetailsByTimestamp {
  [timestamp: string]: {
    address?: string;
    date?: string;
    time?: string;
    latitude?: string;
    longitude?: string;
    file?: string;
    timestamp?: string;
  };
}

export const getTerminalTimeFromTimestamp = (
  timestamp: number,
  terminalOffset: string
): { date: string; time: string } => {
  const dateTime = utcToZonedTime(new Date(timestamp), terminalOffset);
  const date = format(dateTime, appConstants.DATETYPES.MONTHDATEYEAR.format);
  const time = format(dateTime, appConstants.DATETYPES.TIME.format);
  return { date, time };
};

// Finds address from reverse geocoding results based on coordinates
export const getAddressFromReverseGeocodingResults = ({
  latitude,
  longitude,
  reverseGeoCodedLocations,
}: {
  latitude?: string;
  longitude?: string;
  reverseGeoCodedLocations?: any[];
}): any | null => {
  if (latitude && longitude && reverseGeoCodedLocations?.length) {
    const address = reverseGeoCodedLocations.find(
      (obj) =>
        Number(obj.Coords.Lat)?.toFixed(5) === Number(latitude)?.toFixed(5) &&
        Number(obj.Coords.Lon)?.toFixed(5) === Number(longitude)?.toFixed(5)
    ) ?? {};
    return address;
  }
  return null;
};

// Processes image URLs to extract and enrich metadata (timestamp, location, address)
export const processImageMetadata = async (
  images: Image[],
  terminalOffset: string
): Promise<ImageURLDetailsByTimestamp> => {
  const imageURLDetailsByTimestamp: ImageURLDetailsByTimestamp = {};
  
  if (!images.length) {
    return imageURLDetailsByTimestamp;
  }

  // Extract location details from URLs
  const imageURLDetailsList = images
    .map((img) => {
      const { timestamp, latitude, longitude, file } = 
        extractTimeStampLatLongValuesFromURL(img.url);
      return { timestamp, latitude, longitude, file };
    })
    .filter((item) => item.timestamp);

  // Batch reverse geocode locations
  const locationsToGeocode = imageURLDetailsList.filter(
    (location) => location.latitude && location.longitude
  );
  
  const reverseGeoCodedLocations = await ReverseGeocodingBatch(
    locationsToGeocode.map((loc) => ({
      latitude: loc.latitude!,
      longitude: loc.longitude!,
    }))
  );

  // Process each image URL and enrich with metadata
  imageURLDetailsList.forEach((imageURLParams) => {
    const { timestamp, latitude, longitude, file } = imageURLParams;
    
    // Get address from reverse geocoding results
    const addressDetails = getAddressFromReverseGeocodingResults({
      latitude,
      longitude,
      reverseGeoCodedLocations,
    });

    let address = '';
    if (addressDetails?.Address) {
      const {
        Address: { StreetAddress, City, State, Zip, County, Country },
      } = addressDetails;
      address = formatAddress({
        address: StreetAddress,
        address2: City,
        city: County,
        state: State,
        zip: Zip,
        country: Country,
      });
    }

    // Format timestamp to terminal timezone
    const { date, time } = getTerminalTimeFromTimestamp(
      Number(timestamp),
      terminalOffset
    );

    imageURLDetailsByTimestamp[timestamp!] = {
      address,
      date,
      time,
      latitude,
      longitude,
      file,
    };
  });

  return imageURLDetailsByTimestamp;
};
